import * as ko from 'knockout';
import { NflPlayoffTeam } from './models/playoff-team';
import { Enumerable } from '../shared/linq';
import { SchedulePickerBase } from '../shared/schedule-picker-base';
import { SelectionType } from '../shared/enums/selection-type';
import { readConfig, getWebAppBaseUrl } from '../shared/config';
import { ppFetch } from '../shared/pp-fetch';

// ─── PlayoffGame ─────────────────────────────────────────────────────────────

export class PlayoffGame {
  Home?: NflPlayoffTeam;
  Away?: NflPlayoffTeam;
  SaveIndex: number;
  TeamSelected: KnockoutObservable<NflPlayoffTeam>;
  SelectedTeamCss: KnockoutComputed<string>;
  ParentVM: PlayoffsVM;
  Conference: string;
  Round: string;

  constructor(
    away: NflPlayoffTeam,
    home: NflPlayoffTeam,
    conference: string,
    round: string,
    saveIndex: number,
    parentVM: PlayoffsVM,
  ) {
    const self = this;
    self.SaveIndex = saveIndex;
    self.Away = away;
    self.Home = home;
    self.Conference = conference;
    self.Round = round;
    self.TeamSelected = ko.observable(null);
    self.SelectedTeamCss = ko.computed(() => {
      if (
        self.TeamSelected &&
        self.TeamSelected() &&
        self.TeamSelected().Team &&
        self.TeamSelected().Team.TeamAbbreviation
      ) {
        return `${self.TeamSelected().Team.TeamAbbreviation} team-selected`;
      }
      return '';
    });
    self.ParentVM = parentVM;
  }

  pickTeam(team: NflPlayoffTeam, skip: boolean): void {
    const self = this;
    // Unselecting the already-selected team
    if (self.TeamSelected() === team) {
      return self.unselectTeam(team);
    }
    self.TeamSelected(team);

    if (self.Conference === 'AFC') {
      switch (self.Round) {
        case 'WC': {
          if (!skip) { self.ParentVM.PlayoffGames.valueHasMutated(); }
          const dv1 = self.ParentVM.PlayoffGames()['afc-dv-1'];
          if (self.ParentVM.AFCWCWinners()[0]) {
            dv1.Away = self.ParentVM.AFCWCWinners()[0];
          }
          const dv2 = self.ParentVM.PlayoffGames()['afc-dv-2'];
          const dv2Teams: NflPlayoffTeam[] = [];
          if (self.ParentVM.AFCWCWinners()[1]) {
            dv2Teams.push(self.ParentVM.AFCWCWinners()[1]);
          }
          if (self.ParentVM.AFCWCWinners()[2]) {
            dv2Teams.push(self.ParentVM.AFCWCWinners()[2]);
          }
          const sorted = Enumerable.From(dv2Teams).OrderBy((x: NflPlayoffTeam) => x.Seed).ToArray();
          dv2.Home = sorted[0];
          dv2.Away = sorted[1];
          if (!skip) { self.ParentVM.PlayoffGames.valueHasMutated(); }
          break;
        }
        case 'DV': {
          if (!skip) { self.ParentVM.PlayoffGames.valueHasMutated(); }
          const cg = self.ParentVM.PlayoffGames()['afc-cg'];
          if (self.ParentVM.AFCDVWinners()[0]) {
            cg.Away = self.ParentVM.AFCDVWinners()[0];
          }
          if (self.ParentVM.AFCDVWinners()[1]) {
            cg.Home = self.ParentVM.AFCDVWinners()[1];
          }
          if (!skip) { self.ParentVM.PlayoffGames.valueHasMutated(); }
          break;
        }
        case 'CG': {
          if (!skip) { self.ParentVM.PlayoffGames.valueHasMutated(); }
          const sb = self.ParentVM.PlayoffGames()['sb'];
          sb.Away = self.ParentVM.CGWinners()[1];
          sb.Home = self.ParentVM.CGWinners()[0];
          if (!skip) { self.ParentVM.PlayoffGames.valueHasMutated(); }
          break;
        }
        case 'SB': {
          if (!skip) { self.ParentVM.PlayoffGames.valueHasMutated(); }
          break;
        }
      }
    } else {
      switch (self.Round) {
        case 'WC': {
          if (!skip) { self.ParentVM.PlayoffGames.valueHasMutated(); }
          const dv1 = self.ParentVM.PlayoffGames()['nfc-dv-1'];
          if (self.ParentVM.NFCWCWinners()[0]) {
            dv1.Away = self.ParentVM.NFCWCWinners()[0];
          }
          const dv2 = self.ParentVM.PlayoffGames()['nfc-dv-2'];
          const dv2Teams: NflPlayoffTeam[] = [];
          if (self.ParentVM.NFCWCWinners()[1]) {
            dv2Teams.push(self.ParentVM.NFCWCWinners()[1]);
          }
          if (self.ParentVM.NFCWCWinners()[2]) {
            dv2Teams.push(self.ParentVM.NFCWCWinners()[2]);
          }
          const sorted = Enumerable.From(dv2Teams).OrderBy((x: NflPlayoffTeam) => x.Seed).ToArray();
          dv2.Home = sorted[0];
          dv2.Away = sorted[1];
          if (!skip) { self.ParentVM.PlayoffGames.valueHasMutated(); }
          break;
        }
        case 'DV': {
          if (!skip) { self.ParentVM.PlayoffGames.valueHasMutated(); }
          const cg = self.ParentVM.PlayoffGames()['nfc-cg'];
          if (self.ParentVM.NFCDVWinners()[0]) {
            cg.Away = self.ParentVM.NFCDVWinners()[0];
          }
          if (self.ParentVM.NFCDVWinners()[1]) {
            cg.Home = self.ParentVM.NFCDVWinners()[1];
          }
          if (!skip) { self.ParentVM.PlayoffGames.valueHasMutated(); }
          break;
        }
        case 'CG': {
          if (!skip) { self.ParentVM.PlayoffGames.valueHasMutated(); }
          const sb = self.ParentVM.PlayoffGames()['sb'];
          sb.Away = self.ParentVM.CGWinners()[1];
          sb.Home = self.ParentVM.CGWinners()[0];
          if (!skip) { self.ParentVM.PlayoffGames.valueHasMutated(); }
          break;
        }
        case 'SB': {
          if (!skip) { self.ParentVM.PlayoffGames.valueHasMutated(); }
          break;
        }
      }
    }
    self.ParentVM.checkConsistency();
  }

  unselectTeam(_team: NflPlayoffTeam): void {
    const self = this;
    self.TeamSelected(null);
    self.TeamSelected.valueHasMutated();

    if (self.Conference === 'AFC') {
      self.ParentVM.PlayoffGames.valueHasMutated();
      const dv1 = self.ParentVM.PlayoffGames()['afc-dv-1'];
      dv1.Away = self.ParentVM.AFCWCWinners()[0] ?? null;

      const dv2 = self.ParentVM.PlayoffGames()['afc-dv-2'];
      dv2.Away = self.ParentVM.AFCWCWinners()[1] ?? null;
      self.ParentVM.PlayoffGames.valueHasMutated();

      const cg = self.ParentVM.PlayoffGames()['afc-cg'];
      cg.Away = self.ParentVM.AFCDVWinners()[0] ?? null;
      cg.Home = self.ParentVM.AFCDVWinners()[1] ?? null;
      self.ParentVM.PlayoffGames.valueHasMutated();

      const sb = self.ParentVM.PlayoffGames()['sb'];
      sb.Away = self.ParentVM.CGWinners()[1];
      sb.Home = self.ParentVM.CGWinners()[0];
      self.ParentVM.PlayoffGames.valueHasMutated();
    } else {
      self.ParentVM.PlayoffGames.valueHasMutated();
      const dv1 = self.ParentVM.PlayoffGames()['nfc-dv-1'];
      dv1.Away = self.ParentVM.NFCWCWinners()[0] ?? null;

      const dv2 = self.ParentVM.PlayoffGames()['nfc-dv-2'];
      dv2.Away = self.ParentVM.NFCWCWinners()[1] ?? null;
      self.ParentVM.PlayoffGames.valueHasMutated();

      const cg = self.ParentVM.PlayoffGames()['nfc-cg'];
      cg.Away = self.ParentVM.NFCDVWinners()[0] ?? null;
      cg.Home = self.ParentVM.NFCDVWinners()[1] ?? null;
      self.ParentVM.PlayoffGames.valueHasMutated();

      const sb = self.ParentVM.PlayoffGames()['sb'];
      sb.Away = self.ParentVM.CGWinners()[1];
      sb.Home = self.ParentVM.CGWinners()[0];
      self.ParentVM.PlayoffGames.valueHasMutated();
    }
  }
}

// ─── PlayoffsVM ──────────────────────────────────────────────────────────────

export class PlayoffsVM {
  SuperbowlYear: number;
  LeagueName: string;
  PlayoffTeams: KnockoutObservableArray<NflPlayoffTeam>;
  AFCPlayoffTeams: KnockoutComputed<NflPlayoffTeam[]>;
  NFCPlayoffTeams: KnockoutComputed<NflPlayoffTeam[]>;
  AFCWCWinners: KnockoutComputed<NflPlayoffTeam[]>;
  NFCWCWinners: KnockoutComputed<NflPlayoffTeam[]>;
  AFCDVWinners: KnockoutComputed<NflPlayoffTeam[]>;
  NFCDVWinners: KnockoutComputed<NflPlayoffTeam[]>;
  CGWinners: KnockoutComputed<NflPlayoffTeam[]>;
  PlayoffGames: KnockoutObservable<Record<string, PlayoffGame>>;
  SaveValues: KnockoutComputed<string>;
  Loading: KnockoutObservable<boolean>;
  SBHomeScore: KnockoutObservable<number>;
  SBAwayScore: KnockoutObservable<number>;
  ShowScores: KnockoutObservable<boolean>;
  Version: number;

  constructor() {
    const self = this;
    const config = readConfig();

    self.Loading = ko.observable(true);
    self.PlayoffGames = ko.observable({});
    self.PlayoffTeams = ko.observableArray([]);
    self.ShowScores = ko.observable(false);
    self.SBHomeScore = ko.observable(null);
    self.SBAwayScore = ko.observable(null);

    self.SaveValues = ko.computed(() => {
      if (self.Loading()) {
        return '';
      }
      const retStr: number[] = [];
      for (const gameIdx in self.PlayoffGames()) {
        const game = self.PlayoffGames()[gameIdx];
        if (game.Away && game.Home && game.TeamSelected()) {
          if (
            game.Away.Team.TeamAbbreviation ===
            game.TeamSelected().Team.TeamAbbreviation
          ) {
            retStr[game.SaveIndex] = 2;
          } else {
            retStr[game.SaveIndex] = 1;
          }
        } else {
          retStr[game.SaveIndex] = 0;
        }
      }
      const returnString = retStr.join('');
      let newurl =
        window.location.protocol +
        '//' +
        window.location.host +
        window.location.pathname +
        '?L=' +
        returnString;
      if (self.SBHomeScore()) {
        newurl += `&sbhomescore=${self.SBHomeScore()}`;
      }
      if (self.SBAwayScore()) {
        newurl += `&sbawayscore=${self.SBAwayScore()}`;
      }
      window.history.pushState({ path: newurl }, '', newurl);
      return newurl;
    });

    const sbHomeScore = SchedulePickerBase.getQueryVariable('sbhomescore');
    if (sbHomeScore) {
      self.SBHomeScore(parseInt(sbHomeScore, 10));
      self.SBHomeScore.valueHasMutated();
    }
    const sbAwayScore = SchedulePickerBase.getQueryVariable('sbawayscore');
    if (sbAwayScore) {
      self.SBAwayScore(parseInt(sbAwayScore, 10));
      self.SBAwayScore.valueHasMutated();
    }

    self.SBHomeScore.subscribe((newVal: any) => {
      if (self.Loading()) { return; }
      const score = parseInt(newVal, 10);
      if (score > self.SBAwayScore()) {
        self.PlayoffGames()['sb'].TeamSelected(self.PlayoffGames()['sb'].Home);
      } else if (self.SBAwayScore() > score) {
        self.PlayoffGames()['sb'].TeamSelected(self.PlayoffGames()['sb'].Away);
      }
    });

    self.SBAwayScore.subscribe((newVal: any) => {
      if (self.Loading()) { return; }
      const score = parseInt(newVal, 10);
      if (score > self.SBHomeScore()) {
        self.PlayoffGames()['sb'].TeamSelected(self.PlayoffGames()['sb'].Away);
      } else if (self.SBHomeScore() > score) {
        self.PlayoffGames()['sb'].TeamSelected(self.PlayoffGames()['sb'].Home);
      }
    });

    const initialSaveString = SchedulePickerBase.getQueryVariable('L');

    self.AFCPlayoffTeams = ko.computed(() => {
      return Enumerable.From(self.PlayoffTeams())
        .Where((p: NflPlayoffTeam) => p.Team.Conference === 'AFC')
        .OrderBy((x: NflPlayoffTeam) => x.Seed)
        .ToArray();
    });

    self.NFCPlayoffTeams = ko.computed(() => {
      return Enumerable.From(self.PlayoffTeams())
        .Where((p: NflPlayoffTeam) => p.Team.Conference === 'NFC')
        .OrderBy((x: NflPlayoffTeam) => x.Seed)
        .ToArray();
    });

    self.AFCWCWinners = ko.computed(() => {
      const temp: (NflPlayoffTeam | null)[] = [];
      const winner1 = self.PlayoffGames()['afc-wc-1'] && self.PlayoffGames()['afc-wc-1'].TeamSelected();
      const winner2 = self.PlayoffGames()['afc-wc-2'] && self.PlayoffGames()['afc-wc-2'].TeamSelected();
      if (self.Version === 2) {
        const winner3 = self.PlayoffGames()['afc-wc-3'] && self.PlayoffGames()['afc-wc-3'].TeamSelected();
        temp.push(winner3);
      }
      temp.push(winner1 ? winner1 : null);
      temp.push(winner2 ? winner2 : null);
      if (winner1 && winner1.Seed === 3 && !winner2) {
        temp.reverse();
      }
      return Enumerable.From(temp)
        .OrderByDescending((p: NflPlayoffTeam | null) => p ? p.Seed : undefined)
        .ToArray();
    });

    self.NFCWCWinners = ko.computed(() => {
      const temp: (NflPlayoffTeam | null)[] = [];
      const winner1 = self.PlayoffGames()['nfc-wc-1'] && self.PlayoffGames()['nfc-wc-1'].TeamSelected();
      const winner2 = self.PlayoffGames()['nfc-wc-2'] && self.PlayoffGames()['nfc-wc-2'].TeamSelected();
      if (self.Version === 2) {
        const winner3 = self.PlayoffGames()['nfc-wc-3'] && self.PlayoffGames()['nfc-wc-3'].TeamSelected();
        temp.push(winner3);
      }
      temp.push(winner1 ? winner1 : null);
      temp.push(winner2 ? winner2 : null);
      if (winner1 && winner1.Seed === 3 && !winner2) {
        temp.reverse();
      }
      return Enumerable.From(temp)
        .OrderByDescending((p: NflPlayoffTeam | null) => p ? p.Seed : undefined)
        .ToArray();
    });

    self.AFCDVWinners = ko.computed(() => {
      const temp: (NflPlayoffTeam | null)[] = [];
      const winner1 = self.PlayoffGames()['afc-dv-1'] && self.PlayoffGames()['afc-dv-1'].TeamSelected();
      const winner2 = self.PlayoffGames()['afc-dv-2'] && self.PlayoffGames()['afc-dv-2'].TeamSelected();
      temp.push(winner1 ? winner1 : null);
      temp.push(winner2 ? winner2 : null);
      return Enumerable.From(temp)
        .OrderByDescending((p: NflPlayoffTeam | null) => p ? p.Seed : undefined)
        .ToArray();
    });

    self.NFCDVWinners = ko.computed(() => {
      const temp: (NflPlayoffTeam | null)[] = [];
      const winner1 = self.PlayoffGames()['nfc-dv-1'] && self.PlayoffGames()['nfc-dv-1'].TeamSelected();
      const winner2 = self.PlayoffGames()['nfc-dv-2'] && self.PlayoffGames()['nfc-dv-2'].TeamSelected();
      temp.push(winner1 ? winner1 : null);
      temp.push(winner2 ? winner2 : null);
      return Enumerable.From(temp)
        .OrderByDescending((p: NflPlayoffTeam | null) => p ? p.Seed : undefined)
        .ToArray();
    });

    self.CGWinners = ko.computed(() => {
      const winner1 = self.PlayoffGames()['afc-cg'] && self.PlayoffGames()['afc-cg'].TeamSelected();
      const winner2 = self.PlayoffGames()['nfc-cg'] && self.PlayoffGames()['nfc-cg'].TeamSelected();
      const isAfcHome = self.SuperbowlYear % 2 === 0;
      if (isAfcHome) {
        return [winner1 || null, winner2 || null];
      } else {
        return [winner2 || null, winner1 || null];
      }
    });

    (async () => {
      try {
        const ajaxResult = await ppFetch<IAjaxResult<any>>({
          url: `/football/Playoffs/GetPlayoffs/?leagueGuid=${config.leagueGuid}`,
          method: 'GET',
        });
        const model = ajaxResult.Model;
        self.LeagueName = model.League.LeagueName;
        self.SuperbowlYear = model.League.SuperbowlYear;
        if (self.SuperbowlYear >= 2021) {
          self.Version = 2;
        }

        const temp: NflPlayoffTeam[] = [];
        model.PlayoffTeams.map((team: IPlayoffTeamData) => {
          if (team.Team.TeamName === 'Football Team') {
            team.Team.TeamName = 'FT';
          }
          temp.push(new NflPlayoffTeam(team));
        });
        self.PlayoffTeams(temp);

        const games: Record<string, PlayoffGame> = {};
        games['afc-wc-1'] = new PlayoffGame(
          self.AFCPlayoffTeams()[5], self.AFCPlayoffTeams()[2], 'AFC', 'WC', 0, self,
        );
        games['afc-wc-2'] = new PlayoffGame(
          self.AFCPlayoffTeams()[4], self.AFCPlayoffTeams()[3], 'AFC', 'WC', 1, self,
        );
        if (self.Version === 2) {
          games['afc-wc-3'] = new PlayoffGame(
            self.AFCPlayoffTeams()[6], self.AFCPlayoffTeams()[1], 'AFC', 'WC', 2, self,
          );
        }
        games['nfc-wc-1'] = new PlayoffGame(
          self.NFCPlayoffTeams()[5], self.NFCPlayoffTeams()[2], 'NFC', 'WC', 3, self,
        );
        games['nfc-wc-2'] = new PlayoffGame(
          self.NFCPlayoffTeams()[4], self.NFCPlayoffTeams()[3], 'NFC', 'WC', 4, self,
        );
        if (self.Version === 2) {
          games['nfc-wc-3'] = new PlayoffGame(
            self.NFCPlayoffTeams()[6], self.NFCPlayoffTeams()[1], 'NFC', 'WC', 5, self,
          );
        }
        games['afc-dv-1'] = new PlayoffGame(null, self.AFCPlayoffTeams()[0], 'AFC', 'DV', 6, self);
        if (self.Version !== 2) {
          games['afc-dv-2'] = new PlayoffGame(null, self.AFCPlayoffTeams()[1], 'AFC', 'DV', 7, self);
        } else {
          games['afc-dv-2'] = new PlayoffGame(null, null, 'AFC', 'DV', 7, self);
        }
        games['nfc-dv-1'] = new PlayoffGame(null, self.NFCPlayoffTeams()[0], 'NFC', 'DV', 8, self);
        if (self.Version !== 2) {
          games['nfc-dv-2'] = new PlayoffGame(null, self.NFCPlayoffTeams()[1], 'NFC', 'DV', 9, self);
        } else {
          games['nfc-dv-2'] = new PlayoffGame(null, null, 'NFC', 'DV', 9, self);
        }
        games['afc-cg'] = new PlayoffGame(null, null, 'AFC', 'CG', 10, self);
        games['nfc-cg'] = new PlayoffGame(null, null, 'NFC', 'CG', 11, self);
        games['sb'] = new PlayoffGame(null, null, 'AFC', 'SB', 12, self);

        self.PlayoffGames(games);
        self.load(initialSaveString);
        document.querySelectorAll('.hidden').forEach((el) => el.classList.remove('hidden'));
        self.Loading(false);
      } catch (e) {
        console.error('Failed to load playoffs', e);
      }
    })();
  }

  export(): void {
    const self = this;
    const inputs = document.querySelectorAll<HTMLInputElement>('input');
    if (!self.SBHomeScore() && !self.SBAwayScore()) {
      inputs.forEach((el) => { el.style.display = 'none'; });
    }
    (window as any).html2canvas(document.getElementById('playoffs'), {
      background: '#FFF',
      useCORS: true,
      allowTaint: true,
    }).then((canvas: HTMLCanvasElement) => {
      const output = document.getElementById('output');
      output.innerHTML = '';
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d')!;
      const text = 'Created With Playoff Predictors';
      const cw = (tempCanvas.width = canvas.width);
      const ch = (tempCanvas.height = canvas.height + 80);
      tempCtx.drawImage(canvas, 0, 0);
      tempCtx.font = '14px Helvetica';
      let textWidth = tempCtx.measureText(text).width;
      tempCtx.fillStyle = 'black';
      tempCtx.fillText(text, cw - textWidth - 30, ch - 40);
      const text2 = getWebAppBaseUrl();
      textWidth = tempCtx.measureText(text2).width;
      tempCtx.fillStyle = 'black';
      tempCtx.fillText(text2, cw - textWidth - 30, ch - 20);

      const logo = new Image();
      logo.src = '/content/playoff-predictor-color-02.svg';
      logo.width = 64;
      logo.height = 64;
      logo.onload = () => {
        tempCtx.drawImage(logo, cw - textWidth - 100, ch - 60, logo.width * 0.75, logo.height * 0.75);
        output.innerHTML = '';
        output.appendChild(tempCanvas);
      };
    });
    inputs.forEach((el) => { el.style.display = ''; });
  }

  reset(): void {
    const self = this;
    for (const gameIdx in self.PlayoffGames()) {
      const game = self.PlayoffGames()[gameIdx];
      if (game.Round === 'DV') {
        game.Away = null;
      }
      if (game.Round === 'CG' || game.Round === 'SB') {
        game.Away = null;
        game.Home = null;
      }
      game.TeamSelected(null);
      game.TeamSelected.valueHasMutated();
    }
    self.PlayoffGames.valueHasMutated();
  }

  load(saveString?: string): void {
    const self = this;
    const loading = self.Loading();
    if (!saveString) {
      return;
    }
    const map: Record<number, PlayoffGame> = {};
    for (const gameIdx in self.PlayoffGames()) {
      const game = self.PlayoffGames()[gameIdx];
      map[game.SaveIndex] = game;
    }

    for (let i = 0; i < saveString.length; i++) {
      const selection: SelectionType = parseInt(saveString[i]);
      const game: PlayoffGame = map[i];
      if (!game) { continue; }
      switch (selection) {
        case SelectionType.HOME_WIN:
          game.pickTeam(game.Home, loading);
          break;
        case SelectionType.AWAY_WIN:
          game.pickTeam(game.Away, loading);
          break;
        default:
          break;
      }
    }

    for (const gameIdx in map) {
      const game: PlayoffGame = map[gameIdx as unknown as number];
      if (game.Conference === 'AFC') {
        switch (game.Round) {
          case 'WC': {
            self.PlayoffGames.valueHasMutated();
            const dv1 = self.PlayoffGames()['afc-dv-1'];
            if (self.AFCWCWinners()[0]) { dv1.Away = self.AFCWCWinners()[0]; }
            const dv2 = self.PlayoffGames()['afc-dv-2'];
            if (self.AFCWCWinners()[1]) { dv2.Away = self.AFCWCWinners()[1]; }
            self.PlayoffGames.valueHasMutated();
            break;
          }
          case 'DV': {
            self.PlayoffGames.valueHasMutated();
            const cg = self.PlayoffGames()['afc-cg'];
            if (self.AFCDVWinners()[0]) { cg.Away = self.AFCDVWinners()[0]; }
            if (self.AFCDVWinners()[1]) { cg.Home = self.AFCDVWinners()[1]; }
            self.PlayoffGames.valueHasMutated();
            break;
          }
          case 'CG': {
            self.PlayoffGames.valueHasMutated();
            const sb = self.PlayoffGames()['sb'];
            sb.Away = self.CGWinners()[1];
            sb.Home = self.CGWinners()[0];
            self.PlayoffGames.valueHasMutated();
            break;
          }
          case 'SB': {
            self.PlayoffGames.valueHasMutated();
            break;
          }
        }
      } else {
        switch (game.Round) {
          case 'WC': {
            self.PlayoffGames.valueHasMutated();
            const dv1 = self.PlayoffGames()['nfc-dv-1'];
            if (self.NFCWCWinners()[0]) { dv1.Away = self.NFCWCWinners()[0]; }
            const dv2 = self.PlayoffGames()['nfc-dv-2'];
            if (self.NFCWCWinners()[1]) { dv2.Away = self.NFCWCWinners()[1]; }
            self.PlayoffGames.valueHasMutated();
            break;
          }
          case 'DV': {
            self.PlayoffGames.valueHasMutated();
            const cg = self.PlayoffGames()['nfc-cg'];
            if (self.NFCDVWinners()[0]) { cg.Away = self.NFCDVWinners()[0]; }
            if (self.NFCDVWinners()[1]) { cg.Home = self.NFCDVWinners()[1]; }
            self.PlayoffGames.valueHasMutated();
            break;
          }
          case 'CG': {
            self.PlayoffGames.valueHasMutated();
            const sb = self.PlayoffGames()['sb'];
            sb.Away = self.CGWinners()[1];
            sb.Home = self.CGWinners()[0];
            self.PlayoffGames.valueHasMutated();
            break;
          }
          case 'SB': {
            self.PlayoffGames.valueHasMutated();
            break;
          }
        }
      }
    }
    self.Loading(false);
  }

  checkConsistency(): void {
    const self = this;
    const sb = self.PlayoffGames()['sb'];
    const isAfcHome = self.SuperbowlYear % 2 === 0;
    if (isAfcHome) {
      if (sb.Away !== self.PlayoffGames()['nfc-cg'].TeamSelected()) {
        sb.Away = null;
        sb.TeamSelected(null);
      }
      if (sb.Home !== self.PlayoffGames()['afc-cg'].TeamSelected()) {
        sb.Home = null;
        sb.TeamSelected(null);
      }
    } else {
      if (sb.Away !== self.PlayoffGames()['afc-cg'].TeamSelected()) {
        sb.Away = null;
        sb.TeamSelected(null);
      }
      if (sb.Home !== self.PlayoffGames()['nfc-cg'].TeamSelected()) {
        sb.Home = null;
        sb.TeamSelected(null);
      }
    }
  }
}
